import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

// Shared segment save/approve logic for the In-context preview. Mirrors the
// endpoints the Editor uses (PUT /translate with the full array, PUT
// /toggle-approval with { sectionIndex, approved }) so the two surfaces stay
// consistent. Holds per-segment overrides applied optimistically on success.
export default function useSegmentMutations({ projectId, token, initialTranslated }) {
    const [translated, setTranslated] = useState(initialTranslated || []);
    const [overrides, setOverrides] = useState({}); // sectionIndex -> { target?, approved? }
    const [busy, setBusy] = useState(null); // sectionIndex currently saving, or null
    const [saveError, setSaveError] = useState(null);

    // Reset whenever the resolved project (language) or its base data changes.
    useEffect(() => {
        setTranslated(initialTranslated || []);
        setOverrides({});
        setSaveError(null);
    }, [projectId, initialTranslated]);

    const authHeader = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token],
    );

    const saveTranslation = useCallback(async (sectionIndex, value) => {
        if (!projectId) return false;
        setBusy(sectionIndex);
        setSaveError(null);
        const next = [...translated];
        next[sectionIndex] = value;
        try {
            await axios.put(`/api/projects/${projectId}/translate`, { translatedSections: next }, authHeader);
            setTranslated(next);
            setOverrides((o) => ({ ...o, [sectionIndex]: { ...(o[sectionIndex] || {}), target: value } }));
            return true;
        } catch (err) {
            setSaveError(err.response?.data?.msg || 'Could not save translation.');
            return false;
        } finally {
            setBusy(null);
        }
    }, [translated, projectId, authHeader]);

    const toggleApprove = useCallback(async (sectionIndex, approved) => {
        if (!projectId) return false;
        setBusy(sectionIndex);
        setSaveError(null);
        try {
            await axios.put(`/api/projects/${projectId}/toggle-approval`, { sectionIndex, approved }, authHeader);
            setOverrides((o) => ({ ...o, [sectionIndex]: { ...(o[sectionIndex] || {}), approved } }));
            return true;
        } catch (err) {
            setSaveError(err.response?.data?.msg || 'Could not update approval.');
            return false;
        } finally {
            setBusy(null);
        }
    }, [projectId, authHeader]);

    return { overrides, busy, saveError, saveTranslation, toggleApprove };
}
